import { getKeycloak } from '../keycloak';
import { logger } from 'helpers/logger';
import { parseApiResponse } from '../dto/apiResponseDto';
import { EncryptStorage } from 'encrypt-storage';

const encryptStorage = new EncryptStorage('secret-key-value', { prefix: '@mwan' });

const KC_OFFLINE_TOKEN_KEY = 'kc_offline_token';

export const storeKeycloakOfflineToken = (keycloak) => {
    if (keycloak.refreshToken) encryptStorage.setItem(KC_OFFLINE_TOKEN_KEY, keycloak.refreshToken);
};

export const getStoredOfflineToken = () => encryptStorage.getItem(KC_OFFLINE_TOKEN_KEY);

// The file name from a Content-Disposition header: filename* (UTF-8, e.g. Arabic names) wins over
// filename. null when there is none.
const fileNameFromContentDisposition = (header) => {
    if (!header) return null;
    const utf8 = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
    if (utf8) {
        try {
            return decodeURIComponent(utf8[1].trim());
        } catch {
            // fall through to the plain filename
        }
    }
    const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
    return plain ? plain[1].trim() : null;
};

export const makeAuthenticatedRequest = async (url, options = {}) => {
    const isOffline = !navigator.onLine;
    let token;

    if (isOffline) {
        // Offline: use whatever token is in memory — request will fail at the network layer
        // and the SW will queue it in the outbox. The outbox saga refreshes the token before replaying.
        try {
            const keycloak = getKeycloak();
            token = keycloak.token;
        } catch {
            token = null;
        }

        if (!token) {
            throw new Error('No authentication token available offline');
        }
    } else {
        const keycloak = getKeycloak();

        if (!keycloak.authenticated) {
            logger.warn('⚠️ User not authenticated');
            keycloak.login();
            throw new Error('User not authenticated');
        }

        logger.info('🕐 Current time:', new Date().toISOString());
        logger.info('🕐 Token expires at:', new Date(keycloak.tokenParsed.exp * 1000).toISOString());
        logger.info('⏱️ Time until expiry (seconds):', keycloak.tokenParsed.exp - Math.floor(Date.now() / 1000));

        try {
            const refreshed = await keycloak.updateToken(30);
            if (refreshed) {
                logger.info('✅ Token WAS refreshed');
                logger.info('🆕 New token expires at:', new Date(keycloak.tokenParsed.exp * 1000).toISOString());
                storeKeycloakOfflineToken(keycloak);
            } else {
                logger.info('✅ Token was NOT refreshed (still valid)');
            }
        } catch (error) {
            logger.error('❌ Token refresh failed:', error);
            keycloak.login();
            throw new Error('Authentication failed');
        }

        token = keycloak.token;
    }

    // A FormData body (file upload) must not get a Content-Type here: the browser sets
    // "multipart/form-data" itself, with the boundary the server needs to read the parts.
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const defaultHeaders = {
        'Authorization': `Bearer ${token}`,
        ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    };

    logger.info('🔑 Auth headers:', defaultHeaders);

    const config = {
        ...options,
        headers: {
            ...defaultHeaders,
            ...options.headers,
        },
    };

    logger.info('📡 Request URL:', url);
    logger.info('📡 Request config:', config);

    // responseType: "blob" = the endpoint answers with a file, not JSON (option not passed to fetch).
    const { responseType, ...fetchConfig } = config;
    const response = await fetch(url, fetchConfig);

    // SW returns a 202 synthetic response when a mutation is queued offline
    if (response.status === 202) {
        const text = await response.text();
        return text ? JSON.parse(text) : { queued: true };
    }

    // A file download: on success return the file and the name the backend gave it. Errors are still
    // JSON (the backend's message), handled below like any other request.
    if (responseType === 'blob' && response.ok) {
        const blob = await response.blob();
        return {
            blob,
            fileName: fileNameFromContentDisposition(response.headers.get('Content-Disposition')),
            contentType: response.headers.get('Content-Type'),
        };
    }

    const text = await response.text();
    const json_response = text
        ? parseApiResponse(JSON.parse(text))
        : parseApiResponse({ statusCode: response.status, message: response.statusText });

    if (json_response.statusCode < 200 || json_response.statusCode >= 300) {
        // message = the backend's own message, ready to show to the user. The status code and the
        // backend's error list are kept as separate properties (for logs or status-specific handling).
        const error = new Error(json_response.message || `HTTP error! status: ${json_response.statusCode}`);
        error.statusCode = json_response.statusCode;
        error.apiErrors = json_response.errors;
        logger.error(`❌ HTTP ${json_response.statusCode} from ${url}: ${error.message}`, json_response.errors ?? '');
        throw error;
    }

    return json_response;
};
